import {PropTypes} from 'prop-types';
import {getSphereById} from 'virtual-tour-viewer/src/utils/sphereDataHelper';

export default class DirectNavigationModel {
    static getLabelKeyAndTable(navigation, sphereId) {
        let {structure, group, space} = DirectNavigationModel.getActiveElements(
            navigation,
            sphereId
        );
        // eslint-disable-next-line no-nested-ternary
        let labelKey = null;
        let labelTable = null;
        if (space) {
            labelKey = space.spaceType;
            labelTable = space.labelTableOverride || 'spaceLabels';
        } else if (group) {
            labelKey = group.groupType;
            labelTable = 'groupLabels';
        } else if (structure) {
            labelKey = structure.structureType;
            labelTable = 'structureLabels';
        }

        if (!labelKey || !labelTable) {
            labelKey = 'directNav.exploreMore';
            labelTable = 'messages';
        }
        return {labelKey, labelTable};
    }

    static getActiveElements(navigation, sphereId) {
        let structures = navigation.structures || null;
        let groups = !structures
            ? navigation.groups
            : structures
                  .filter((element) => !!element.groups)
                  .reduce(
                      (accumulator, currentValue) => accumulator.concat(currentValue.groups),
                      []
                  );
        let spaces = !groups
            ? navigation.spaces
            : groups
                  .filter((element) => !!element.spaces)
                  .reduce(
                      (accumulator, currentValue) => accumulator.concat(currentValue.spaces),
                      []
                  );
        let space = null;
        let group = null;
        let structure = null;
        if (spaces) {
            space = spaces.find(
                (element) =>
                    !![element.landingSphere]
                        .concat(element.spheres || [])
                        .find((sphere) => sphere.id === sphereId)
            );
            spaces.forEach((element) => {
                element.active = false;
            });
        }

        if (groups) {
            if (space) {
                group = groups.find(
                    (element) =>
                        !!element.spaces &&
                        !!element.spaces.find((otherSpace) => otherSpace === space)
                );
                // Current sphere is unlabeled; find the group with the current sphere
            } else {
                group = groups.find(
                    (element) =>
                        !!element.unlabeledSpheres &&
                        !!element.unlabeledSpheres.find((sphere) => sphere === sphereId)
                );
            }
            groups.forEach((element) => {
                element.active = false;
            });
        }

        if (structures) {
            if (group) {
                structure = structures.find(
                    (element) =>
                        !!element.groups &&
                        !!element.groups.find((otherFloor) => otherFloor === group)
                );
            }
        }

        if (!structure && !group && !space) {
            if (structures && structures.length) {
                structure = structures[0];
                group = structure.groups.length ? structure.groups[0] : null;
            } else if (groups && groups.length) {
                group = groups[0];
            }
        }

        return {
            structure,
            group,
            space,
        };
    }

    static getNavigationFromGroups(groups = [], tourData) {
        let spaces = [];
        for (const group of groups) {
            let landingSphere = this.getLandingSphereForSpaceInGroup(tourData, group);
            if (landingSphere) {
                spaces.push({
                    id: group.groupId,
                    spaceType: group.translationKey,
                    labelTableOverride: 'groupLabels',
                    landingSphere,
                    spheres: group.sphereIds
                        .filter((id) => id !== landingSphere.id)
                        .map((sphereId) => {
                            return {
                                id: sphereId,
                            };
                        }),
                });
            }
        }
        return {spaces};
    }

    static getLandingSphereForSpaceInGroup(tourData, group) {
        let startSphereId = group.startSphereId;

        // startSphere for group is ideal to be landing sphere
        let landingSphere = getSphereById(tourData, startSphereId);
        if (landingSphere) {
            return {
                id: landingSphere.sphereId,
                imageURI: landingSphere.image,
                mediaGatewayURL: landingSphere.mediaGatewayURL,
            };
        } else if (group.sphereIds.length > 1) {
            // key sphere doesn't exist so grab a different sphere to use as key sphere
            let spheresIdsForGroup = group.sphereIds;
            for (const sphereId of spheresIdsForGroup) {
                landingSphere = getSphereById(tourData, sphereId);
                if (landingSphere) {
                    return {
                        id: landingSphere.sphereId,
                        imageURI: landingSphere.image,
                        mediaGatewayURL: landingSphere.mediaGatewayURL,
                    };
                }
            }
        }

        // no spheres in group can be the key sphere
        return null;
    }

    static doesNavigationHaveGroups(navigation) {
        return navigation && navigation.groups;
    }

    static doesNavigationHaveSpaces(navigation) {
        return navigation && navigation.spaces;
    }

    static doesNavigationHaveGroupsAndSpaces(navigation) {
        return (
            this.doesNavigationHaveGroups(navigation) && this.doesNavigationHaveSpaces(navigation)
        );
    }

    static sphereType = PropTypes.shape({
        id: PropTypes.string.isRequired,
        imageURI: PropTypes.string,
        mediaGatewayURL: PropTypes.string,
        heading: PropTypes.number,
    });

    static spaceType = PropTypes.shape({
        id: PropTypes.string.isRequired,
        spaceType: PropTypes.string.isRequired, // Enum to map to a translation key.
        labelTableOverride: PropTypes.string,
        landingSphere: DirectNavigationModel.sphereType.isRequired,
        spheres: PropTypes.arrayOf(DirectNavigationModel.sphereType),
        active: PropTypes.bool,
    });

    static groupType = PropTypes.shape({
        id: PropTypes.string.isRequired,
        groupType: PropTypes.string.isRequired, // Enum to map to a translation key.
        spaces: PropTypes.arrayOf(DirectNavigationModel.spaceType),
        active: PropTypes.bool,
    });

    static structureType = PropTypes.shape({
        id: PropTypes.string.isRequired,
        structureType: PropTypes.string.isRequired, // Enum to map to a translation key.
        environmentType: PropTypes.string.isRequired, // Enum to map to a translation key.
        floors: PropTypes.arrayOf(DirectNavigationModel.groupType),
        active: PropTypes.bool,
    });

    static navigationType = PropTypes.shape({
        structures: PropTypes.arrayOf(DirectNavigationModel.structureType),
        groups: PropTypes.arrayOf(DirectNavigationModel.groupType),
        spaces: PropTypes.arrayOf(DirectNavigationModel.spaceType),
        validTourDataVersions: PropTypes.arrayOf(PropTypes.string),
    });
}
